<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('stockTransferReport.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('stockTransferReport.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3">
        <div class="form-group">
          <ts-card>
            <ts-card-wrapper>
              <ts-loading-banner :loading="loading">
                <form>
                  <fieldset>
                    <legend>{{ $t('stockTransferReport.criteria') }}</legend>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('stockTransferReport.transferDate')
                          }}</label>
                          <a-range-picker
                            :allowClear="false"
                            format="DD-MM-YYYY"
                            :defaultValue="rangeDate"
                            :defaultPickerValue="rangeDate"
                            @change="dateRangeChange"
                            :ranges="{
                              Today: [moment(), moment()],
                              'This Month': [
                                moment().startOf('month'),
                                moment().endOf('month')
                              ]
                            }"
                          >
                            <template slot="renderExtraFooter"></template>
                          </a-range-picker>
                          <div class="validate" v-if="errors.has('date_range')">
                            {{ errors.first('date_range') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('stockTransferReport.branchName')
                          }}</label>
                          <a-select
                            v-model="model.branch_id"
                            :options="branches"
                            class="ts-w-full"
                            mode="multiple"
                            placeholder="All"
                            @change="getLocations"
                            showSearch
                            :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                          ></a-select>
                          <div class="validate" v-if="errors.has('branch_id')">
                            {{ errors.first('branch_id') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('stockTransferReport.warehouse')
                          }}</label>
                          <a-select
                            v-model="model.location_id"
                            :options="locations"
                            mode="multiple"
                            placeholder="All"
                            class="ts-w-full"
                            showSearch
                            :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                          ></a-select>
                          <div
                            class="validate"
                            v-if="errors.has('location_id')"
                          >
                            {{ errors.first('location_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{
                            $t('stockTransferReport.transferStatus')
                          }}</label>
                          <select
                            class="form-control"
                            v-model="model.status_id"
                          >
                            <option :value="null">
                              {{ $t('All') }}
                            </option>
                            <option
                              v-for="(s, index) in transferStatus"
                              :key="index"
                              :value="s.value"
                            >
                              {{ s.label }}
                            </option>
                          </select>
                          <div class="validate" v-if="errors.has('status_id')">
                            {{ errors.first('status_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{
                            $t('stockTransferReport.transferBy')
                          }}</label>
                          <a-select
                            v-model="model.transfer_by"
                            :options="transferBy"
                            show-search
                            allow-clear
                            class="ts-w-full"
                            :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                          ></a-select>
                          <div
                            class="validate"
                            v-if="errors.has('transfer_by')"
                          >
                            {{ errors.first('transfer_by') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{
                            $t('stockTransferReport.productGroup')
                          }}</label>
                          <a-select
                            v-model="model.pro_group_id"
                            :options="productGroups"
                            show-search
                            mode="multiple"
                            class="ts-w-full"
                            placeholder="All"
                            :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                          ></a-select>
                          <div
                            class="validate"
                            v-if="errors.has('pro_group_id')"
                          >
                            {{ errors.first('pro_group_id') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{ $t('stockTransferReport.product') }}</label>
                          <a-select
                            v-model="model.product_id"
                            :loading="on_search_product"
                            :options="productList"
                            mode="multiple"
                            placeholder="All"
                            :maxTagCount="1"
                            :maxTagTextLength="10"
                            @search="onProductSearch"
                            :filter-option="true"
                            :not-found-content="null"
                            :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                            class="ts-w-full"
                          ></a-select>
                          <div
                            class="validate"
                            v-if="errors.has('location_id')"
                          >
                            {{ errors.first('location_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <label>{{
                          $t('stockTransferReport.reportType')
                        }}</label>
                        <div class="form-group">
                          <a-radio-group
                            v-model="model.transfer_type"
                            name="radioGroup"
                          >
                            <a-radio value="transfered">Transfered</a-radio>
                            <a-radio value="received">Received</a-radio>
                          </a-radio-group>
                        </div>
                      </div>
                    </div>

                    <div class="row form-group">
                      <a-radio-group
                        v-model="model.report_type"
                        class="col-md-12"
                      >
                        <a-radio-button value="1" class="col-md-6"
                          >Summary</a-radio-button
                        >
                        <a-radio-button value="2" class="col-md-6"
                          >Details</a-radio-button
                        >
                      </a-radio-group>
                    </div>

                    <div class="row">
                      <div class="col-md-6 ts-text-right">
                        <ts-button
                          outline
                          color="success"
                          class="btn-block"
                          @click.prevent="exportExcel"
                          :waiting="exporting"
                          ><i class="far fa-file-excel"></i>
                          {{ $t('export') }}</ts-button
                        >
                      </div>
                      <div class="col-md-6 ts-text-right">
                        <ts-button
                          color="primary"
                          outline
                          class="btn-block"
                          @click.prevent="preview"
                          :waiting="waiting"
                          ><i class="far fa-file-pdf"></i>
                          {{ $t('preview') }}</ts-button
                        >
                      </div>
                    </div>
                  </fieldset>
                </form>
              </ts-loading-banner>
            </ts-card-wrapper>
          </ts-card>
        </div>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('stockTransferReport.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'
import { trim, uniqBy } from 'lodash'

export default {
  name: 'stockTransfer',
  data() {
    return {
      waiting: false,
      loading: false,
      exporting: false,
      on_search_product: false,
      errors: new Errors(),
      stock_transfers: [],
      productList: [],
      branches: [],
      locations: [],
      transferBy: [],
      productGroups: [],
      src: '',
      rangeDate: [moment(), moment()],
      model: {
        transfer_type: 'transfered',
        branch_id: undefined,
        location_id: undefined,
        status_id: null,
        report_type: '1',
        pro_group_id: undefined,
        product_id: undefined
      },
      transferStatus: [
        {
          value: 1,
          label: 'Pending'
        },
        {
          value: 2,
          label: 'Received'
        },
        {
          value: 3,
          label: 'Rejected'
        }
      ]
    }
  },
  async created() {
    await this.getProductGroup()
    await this.getBranchs()
    this.loading = false
  },
  methods: {
    ...mapActions('report/resource', [
      'getTransferNumber',
      'getBranch',
      'productGroup',
      'productProfileLocation',
      'getUsers'
    ]),
    moment,
    async getBranchs() {
      await this.getBranch()
        .then(async response => {
          this.branches = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
          await this.getLocations(false)
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    async getLocations($check) {
      if ($check) {
        this.model.location_id = undefined
      }
      this.locations = []
      await this.$store
        .dispatch('report/inventory/getLocation', {
          branch_id: this.model.branch_id
        })
        .then(async response => {
          this.locations = response.data.map(el => ({
            value: el.location_id,
            label: el.location_name_en
          }))
          await this.getUser()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    async getUser () {
      await this.getUsers()
        .then(response => {
          this.transferBy = response.data.map(el => ({
            value: el.user_id,
            label: el.name
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    async getProductGroup () {
      this.productList = undefined
      await this.productGroup()
        .then(response => {
          this.productGroups = response.data.map(el => ({
            value: el.pro_group_id,
            label: el.pro_group_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    onProductSearch: _.debounce(function (value) {
      this.on_search_product = true
      this.productProfileLocation({
        filter: {
          search: value,
          pro_group_id: this.model.pro_group_id
        }
      })
        .then(response => {
          this.productList = response.data.map(el => ({
            value: el.product_id,
            label: el.barcode + '|' + el.product_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.on_search_product = false
        })
    }, 500),
    preview() {
      this.errors = new Errors()
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('report/inventory/stockTransfer', this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/inventory/stockTransferExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    dateRangeChange(rangeDate) {
      this.$store.commit(
        'report/inventory/SET_DATE_FROM',
        rangeDate[0].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'report/resource/SET_DATE_FROM',
        rangeDate[0].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'report/inventory/SET_DATE_TO',
        rangeDate[1].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'report/resource/SET_DATE_TO',
        rangeDate[1].format('DD-MM-YYYY')
      )
    }
  },
  watch: {},
  beforeRouteLeave(to, from, next) {
    this.$store.commit(
      'report/inventory/SET_DATE_FROM',
      moment().format('DD-MM-YYYY')
    )
    this.$store.commit(
      'report/inventory/SET_DATE_TO',
      moment().format('DD-MM-YYYY')
    )
    this.$store.commit(
      'report/resource/SET_DATE_FROM',
      moment().format('DD-MM-YYYY')
    )
    this.$store.commit(
      'report/resource/SET_DATE_TO',
      moment().format('DD-MM-YYYY')
    )
    next()
  }
}
</script>
